import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CommonInput from '../form/common-input.component';
import CommonPagination from '../pagination/pagination.component';
import LoadingOverlayButton from '../button/loading-overlay-button.component';
import type { CloudDishLibraryPixabayHit } from '../../request/api/rbp/cloud-dish-library';
import { PIXABAY_LANGUAGE_OPTIONS } from '../../utils/image-file';
import { ReactComponent as SearchIcon } from '../../static/images/svg/ic_round-search.svg';
import { ReactComponent as VisibilityIcon } from '../../static/images/svg/ic_round-visibility.svg';
import i18n from '../../i18n';

/** 网络图片来源面板的样式类名集合。 */
type NetworkImageSourcePanelClasses = Record<string, string>;

/** 网络图片来源面板入参。 */
interface NetworkImageSourcePanelProps {
    /** 页面样式类名。 */
    classes: NetworkImageSourcePanelClasses;
    /** 粘贴图片链接。 */
    pasteUrl: string;
    /** 更新粘贴图片链接。 */
    setPasteUrl: (value: string) => void;
    /** 粘贴链接拉取后的预览地址。 */
    pastePreviewUrl: string;
    /** 搜图关键词。 */
    searchQ: string;
    /** 更新搜图关键词。 */
    setSearchQ: (value: string) => void;
    /** 搜图语言。 */
    searchLang: string;
    /** 更新搜图语言。 */
    setSearchLang: (value: string) => void;
    /** 粘贴链接拉取是否进行中。 */
    pasteFetchBusy: boolean;
    /** Pixabay 搜索 / 翻页是否进行中。 */
    searchBusy: boolean;
    /** 拉取粘贴链接图片。 */
    onFetchFromPasteUrl: () => void;
    /** 执行 Pixabay 搜索。 */
    onSearch: (page: number) => void;
    /** 搜图是否已经执行过。 */
    hasSearched: boolean;
    /** 搜图命中结果。 */
    hits: CloudDishLibraryPixabayHit[];
    /** 当前选中的搜图结果 ID。 */
    selectedHitId: number | null;
    /** 选中搜图结果。 */
    onSelectHit: (hit: CloudDishLibraryPixabayHit) => void;
    /** 打开图片预览。 */
    onPreview: (url: string) => void;
    /** Pixabay 署名。 */
    attribution: string;
    /** 当前页码。 */
    page: number;
    /** 搜图总页数。 */
    totalPages: number;
}

/**
 * 展示网络粘贴、搜图、分页与预览操作。
 * @param props - 网络图片来源面板参数
 * @returns 网络图片来源面板
 */
export default function NetworkImageSourcePanel(
    props: Readonly<NetworkImageSourcePanelProps>,
): React.ReactElement {
    const {
        classes,
        pasteUrl,
        setPasteUrl,
        pastePreviewUrl,
        searchQ,
        setSearchQ,
        searchLang,
        setSearchLang,
        pasteFetchBusy,
        searchBusy,
        onFetchFromPasteUrl,
        onSearch,
        hasSearched,
        hits,
        selectedHitId,
        onSelectHit,
        onPreview,
        attribution,
        page,
        totalPages,
    } = props;

    return (
        <Box className={classes.networkSection}>
            <Box className={classes.networkBlock}>
                <Typography variant="subtitle1" className={classes.networkBlockTitle}>
                    {i18n.t('image_source_picker_label_paste_url')}
                </Typography>
                <Box className={classes.urlRow}>
                    <Box className={classes.networkInputWrap}>
                        <CommonInput
                            inputType="INPUT"
                            value={pasteUrl}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setPasteUrl(event.target.value)
                            }
                            placeholder={i18n.t('image_source_picker_placeholder_paste_url')}
                        />
                    </Box>
                    <LoadingOverlayButton
                        color="primary"
                        variant="contained"
                        className={classes.networkActionButton}
                        loading={pasteFetchBusy}
                        onClick={onFetchFromPasteUrl}
                    >
                        {i18n.t('image_source_picker_button_fetch_image')}
                    </LoadingOverlayButton>
                </Box>
                {pastePreviewUrl ? (
                    <Box className={classes.pastePreview}>
                        <img
                            className={classes.hitImg}
                            src={pastePreviewUrl}
                            alt={i18n.t('image_source_picker_label_paste_url')}
                        />
                    </Box>
                ) : null}
            </Box>

            <Box className={classes.networkBlock}>
                <Typography variant="subtitle1" className={classes.networkBlockTitle}>
                    {i18n.t('image_source_picker_label_search')}
                </Typography>
                <Box className={classes.urlRow}>
                    <Box className={classes.networkInputWrap}>
                        <CommonInput
                            inputType="INPUT"
                            value={searchQ}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setSearchQ(event.target.value)
                            }
                            placeholder={i18n.t('image_source_picker_placeholder_search')}
                        />
                    </Box>
                    <Box className={classes.networkLanguageSelect}>
                        <CommonInput
                            inputType="SELECT"
                            label={i18n.t('image_source_picker_label_search_language')}
                            value={searchLang}
                            items={PIXABAY_LANGUAGE_OPTIONS}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setSearchLang(String(event.target.value))
                            }
                        />
                    </Box>
                    <LoadingOverlayButton
                        color="primary"
                        variant="contained"
                        className={classes.networkActionButton}
                        loading={searchBusy}
                        startIcon={<SearchIcon width={32} height={32} />}
                        onClick={() => onSearch(1)}
                    >
                        {i18n.t('image_source_picker_button_search')}
                    </LoadingOverlayButton>
                </Box>
            </Box>

            <div>
                {hasSearched && hits.length > 0 ? (
                    <Box className={classes.hitGrid}>
                        {hits.map((hit) => (
                            <Box
                                key={hit.id}
                                className={`${classes.hitCell} ${
                                    selectedHitId === hit.id ? classes.hitCellSelected : ''
                                }`}
                            >
                                <Button
                                    component="div"
                                    className={classes.hitSelectButton}
                                    onClick={() => onSelectHit(hit)}
                                    aria-label={hit.tags || String(hit.id)}
                                >
                                    <img
                                        className={classes.hitImg}
                                        src={hit.webformatURL}
                                        alt={hit.tags || ''}
                                    />
                                </Button>
                                <IconButton
                                    className={classes.hitPreviewButton}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onPreview(hit.webformatURL);
                                    }}
                                    aria-label={`${i18n.t('image_source_picker_label_search')}: ${
                                        hit.tags || hit.id
                                    }`}
                                >
                                    <VisibilityIcon width={24} height={24} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                ) : hasSearched ? (
                    <Typography variant="body1" className={classes.emptySearchResult}>
                        {i18n.t('general_text_no_data')}
                    </Typography>
                ) : null}

                {attribution ? (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        className={classes.attribution}
                    >
                        {attribution}
                    </Typography>
                ) : null}
            </div>

            {totalPages > 1 ? (
                <Box className={classes.paginationWrap}>
                    <CommonPagination count={totalPages} page={page} onChange={onSearch} />
                </Box>
            ) : null}
        </Box>
    );
}
